import React, { useRef, useState, useEffect } from "react"
import gql from 'graphql-tag'
import { graphql } from '@apollo/client/react/hoc'
import { Popover, PopoverBody, Modal, ModalHeader, ModalBody, ModalFooter, Button, Input } from "reactstrap"
import { useParams } from "react-router-dom"
import withApolloClient from '../../withApolloClient'
import RenameColumnPopOver from "./RenameColumnPopOver"
import ConfirmationPopup from "../../Material/ConfirmationPopup"
import "./RenderEditColumnPopover.css"

const ColumnElementTypesQuery = gql`query ColumnElementTypesQuery {
    __type(name:"TaskRabbitColumnElementTypeChoices") {
        enumValues {
            name
            description
        }
    }
}`

const UpdateBoardColumnMutation = gql`
    mutation UpdateBoardColumnMutation($input: UpdateBoardColumnMutationInput!){
        updateBoardColumn(input:$input){
            ok
            errors{
                field
                messages
            }
        }
    }
`

const CreateBoardColumnMutation = gql`mutation CreateBoardColumnMutation($input: CreateBoardColumnMutationInput!){
    createBoardColumn(input: $input){
        ok
        errors{
            field
            messages
        }
    }
}`

const buildEnumOptionRows = (options = []) => options.map((option, index) => ({
    rowId: `existing-option-${index}`,
    value: option || "",
    originalValue: option || "",
}))

const RenderEditColumnPopover = ({ header, apolloClient, columnElementTypes, refetchBoardQuery, refetchTasksQuery }) => {
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [showTypeMenu, setShowTypeMenu] = useState(false)
    const [submenuPosition, setSubmenuPosition] = useState("right")
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [columnToArchive, setColumnToArchive] = useState(null)
    const [selectedType, setSelectedType] = useState("")
    const [renameOpen, setRenameOpen] = useState(false)
    const [editOptionsOpen, setEditOptionsOpen] = useState(false)
    const [enumOptionRows, setEnumOptionRows] = useState([])
    const [initialEnumOptions, setInitialEnumOptions] = useState([])

    const menuItemRef = useRef(null)
    const optionRowCounter = useRef(0)
    const { boardId } = useParams()

    useEffect(() => {
        if (showTypeMenu && menuItemRef.current) {
            const rect = menuItemRef.current.getBoundingClientRect()
            const viewportWidth = window.innerWidth
            const spaceRight = viewportWidth - rect.right
            const submenuWidth = 230
            setSubmenuPosition(spaceRight < submenuWidth ? "left" : "right")
        }
    }, [showTypeMenu])

    const refetchColumnViews = async () => {
        if (refetchBoardQuery) {
            await refetchBoardQuery()
        }
        if (refetchTasksQuery) {
            await refetchTasksQuery()
        }
    }

    const resetEnumOptionEditor = () => {
        setEditOptionsOpen(false)
        setInitialEnumOptions([])
        setEnumOptionRows([])
        setError("")
    }

    const openEnumOptionEditor = () => {
        const currentOptions = header?.columnNode?.allowedOptions || []
        setInitialEnumOptions(currentOptions)
        setEnumOptionRows(buildEnumOptionRows(currentOptions))
        setEditOptionsOpen(true)
        setPopoverOpen(false)
        setError("")
    }

    const handleAddOptionRow = () => {
        const nextRowId = `new-option-${optionRowCounter.current++}`
        setEnumOptionRows((prevRows) => [...prevRows, { rowId: nextRowId, value: "", originalValue: null }])
    }

    const handleOptionValueChange = (rowId, value) => {
        setEnumOptionRows((prevRows) => prevRows.map((row) => row.rowId === rowId ? { ...row, value } : row))
    }

    const handleRemoveOptionRow = (rowId) => {
        setEnumOptionRows((prevRows) => prevRows.filter((row) => row.rowId !== rowId))
    }

    const handleBoardColumnUpdate = async ({ action, columnId, newTitle, newType, columnName, allowedOptions, optionChanges }) => {
        if (!apolloClient) return setError("GQL Client not found")
        if (!columnId) return setError("Column ID is required")

        setLoading(true)
        setError("")

        try {
            let input = { columnId }

            switch (action) {
                case "rename":
                    if (!newTitle?.trim()) throw new Error("Column name cannot be empty")
                    input.title = newTitle.trim()
                    break

                case "archive":
                    input.isArchived = true
                    break

                case "editEnumOptions":
                    input.allowedOptions = allowedOptions
                    input.optionChanges = JSON.stringify(optionChanges || {})
                    break

                case "changeType":
                    setLoading(true)
                    if (!newType) throw new Error("Please select a type")

                    await apolloClient.mutate({
                        mutation: UpdateBoardColumnMutation,
                        variables: { input: { columnId, isArchived: true } },
                    })

                    await apolloClient.mutate({
                        mutation: CreateBoardColumnMutation,
                        variables: {
                            input: {
                                boardId,
                                columnType: newType,
                                elementType: newType,
                                title: columnName || "New Column",
                                isRequired: false,
                            },
                        },
                    })

                    setShowTypeMenu(false)
                    setSelectedType("")
                    await refetchColumnViews()
                    setLoading(false)
                    return

                default:
                    throw new Error("Invalid action")
            }

            const { data } = await apolloClient.mutate({
                mutation: UpdateBoardColumnMutation,
                variables: { input },
            })

            if (!data?.updateBoardColumn?.ok) {
                throw new Error(
                    data?.updateBoardColumn?.errors?.[0]?.messages?.[0] || "Column update failed"
                )
            }

            setPopoverOpen(false)
            setConfirmOpen(false)
            setColumnToArchive(null)
            setSelectedType("")
            resetEnumOptionEditor()
            await refetchColumnViews()
        } catch (err) {
            setError(err.message || "Error updating column")
        } finally {
            setLoading(false)
        }
    }

    const handleSaveEnumOptions = async () => {
        const sanitizedRows = enumOptionRows.map((row) => ({
            ...row,
            value: row.value.trim(),
        }))
        const nextAllowedOptions = sanitizedRows.map((row) => row.value).filter(Boolean)

        if (!nextAllowedOptions.length) {
            setError("At least one option is required")
            return
        }

        const duplicateOptions = nextAllowedOptions.filter((option, index) => nextAllowedOptions.indexOf(option) !== index)
        if (duplicateOptions.length) {
            setError(`Option '${duplicateOptions[0]}' already exists`)
            return
        }

        const renamedOptions = {}
        const removedOptions = []

        initialEnumOptions.forEach((originalOption) => {
            const matchingRow = sanitizedRows.find((row) => row.originalValue === originalOption)
            if (!matchingRow || !matchingRow.value) {
                removedOptions.push(originalOption)
                return
            }
            if (matchingRow.value !== originalOption) {
                renamedOptions[originalOption] = matchingRow.value
            }
        })

        await handleBoardColumnUpdate({
            action: "editEnumOptions",
            columnId: header?.columnNode?.id,
            allowedOptions: nextAllowedOptions,
            optionChanges: {
                renamedOptions,
                removedOptions,
            },
        })
    }

    return (
        <>
            <i id={`${header.id}Ellipsis`} title="More Options" className="fa fa-ellipsis-h task-ellipsis-icon" onClick={() => setPopoverOpen(!popoverOpen)} />

            <Popover trigger="legacy" className="task-header-menu-popover" placement="right" isOpen={popoverOpen} target={`${header.id}Ellipsis`} toggle={() => setPopoverOpen(false)}
            >
                <PopoverBody className="p-0">
                    <ul className="task-header-menu">
                        <li className="menu-item" onClick={() => {
                            setRenameOpen(true)
                            setPopoverOpen(false)
                        }}>
                            <i className="fa fa-pencil-square-o" />Rename
                        </li>

                        {header?.columnNode?.columnType === "ENUM" && (
                            <li className="menu-item" onClick={openEnumOptionEditor}>
                                <i className="fa fa-list-ul" />Edit Options
                            </li>
                        )}

                        <li className="menu-item submenu-trigger" ref={menuItemRef} onClick={() => setShowTypeMenu(prev => !prev)}                        >
                            <i className="fa fa-exchange" /> Change Column Type
                            <i className="fa fa-angle-right submenu-arrow" />

                            {showTypeMenu && (
                                <div className={`submenu-popover submenu-${submenuPosition}`}>
                                    <div className="change-type-card">
                                        <div className="change-type-list">
                                            {columnElementTypes?.filter((t) => t.name !== "MIRROR").map((t, index) => {
                                                const type = t.name
                                                const icons = ["fa-font", "fa-list-ol", "fa-list", "fa-hashtag", "fa-calendar", "fa-calendar-o", "fa-clock-o", "fa-database", "fa-tag",]
                                                const icon = icons[index % icons.length]
                                                return (
                                                    <button
                                                        key={type}
                                                        type="button"
                                                        className={`change-type-item-btn`}
                                                        disabled={loading}
                                                        onClick={() => {
                                                            setSelectedType(type)
                                                            handleBoardColumnUpdate({
                                                                action: "changeType",
                                                                columnId: header?.columnNode?.id,
                                                                columnName: header.title,
                                                                newType: type,
                                                            })
                                                            setShowTypeMenu(false)
                                                        }}
                                                    >
                                                        <div className="type-icon-box">
                                                            <i className={`fa ${icon}`} />
                                                        </div>
                                                        <span className="type-label">{type}</span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </li>

                        <li className="menu-item" onClick={() => {
                            setColumnToArchive(header?.columnNode?.id)
                            setConfirmOpen(true)
                            setPopoverOpen(false)
                        }}>
                            <i className="fa fa-archive" /> Archive
                        </li>
                    </ul>
                </PopoverBody>
            </Popover>

            {renameOpen && (
                <RenameColumnPopOver isOpen={renameOpen} toggle={() => setRenameOpen(false)} target={`${header.id}Ellipsis`} column={header} refetchBoardQuery={refetchBoardQuery} />
            )}

            <Modal isOpen={editOptionsOpen} toggle={resetEnumOptionEditor} centered className="edit-enum-options-modal">
                <ModalHeader toggle={resetEnumOptionEditor}>Edit Enum Options</ModalHeader>
                <ModalBody>
                    <div className="text-muted small mb-3">
                        Update this column's options. Renamed options keep existing task values in sync, and removed options clear those task values.
                    </div>
                    {error && <div className="text-danger mb-3">{error}</div>}
                    <div className="enum-options-editor">
                        {enumOptionRows.map((row, index) => (
                            <div key={row.rowId} className="enum-option-row">
                                <Input
                                    type="text"
                                    value={row.value}
                                    onChange={(e) => handleOptionValueChange(row.rowId, e.target.value)}
                                    placeholder={`Option ${index + 1}`}
                                />
                                <Button
                                    color="danger"
                                    type="button"
                                    className="enum-option-remove-btn"
                                    onClick={() => handleRemoveOptionRow(row.rowId)}
                                    disabled={loading}
                                >
                                    Remove
                                </Button>
                            </div>
                        ))}
                    </div>
                    <Button color="secondary" type="button" className="mt-2 w-100" onClick={handleAddOptionRow} disabled={loading}>
                        Add Option
                    </Button>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" type="button" onClick={resetEnumOptionEditor} disabled={loading}>Cancel</Button>
                    <Button color="primary" type="button" onClick={handleSaveEnumOptions} disabled={loading}>
                        {loading ? "Saving..." : "Save Options"}
                    </Button>
                </ModalFooter>
            </Modal>

            <ConfirmationPopup
                open={confirmOpen}
                message="Are you sure you want to archive this column?"
                loading={loading}
                handleClose={() => {
                    setConfirmOpen(false)
                    setError("")
                }}
                action="Yes"
                confirmAction={() => handleBoardColumnUpdate({ action: "archive", columnId: columnToArchive })}
                color="danger"
                error={error}
            />
        </>
    )
}

export default graphql(ColumnElementTypesQuery, {
    props: ({ data: { __type, loading } }) => ({
        columnElementTypes: __type?.enumValues || [],
        columnElementTypesLoading: loading,
    }),
})(withApolloClient(RenderEditColumnPopover))
